import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { AIUsageDashboardService } from "../../services/Dashboards/AIUsageDashboardService";

interface DashboardFilters {
  collaborator_id?: number | null;
  period?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  category_id?: number | null;
}

interface ContextProps {
  filters: DashboardFilters;
  setFilters: Dispatch<SetStateAction<DashboardFilters>>;

  kpis: any | null;
  collaborators: any[];
  categories: any[];

  isLoadingKpis: boolean;
  isLoadingCollaborators: boolean;
  isLoading: boolean;

  error: string;
  setError: Dispatch<SetStateAction<string>>;

  fetchAllData: () => Promise<void>;
}

export const AIUsageDashboardContext = createContext<ContextProps>(
  {} as ContextProps
);

interface AIUsageDashboardProviderProps {
  children: ReactNode;
}

export const AIUsageDashboardProvider = ({
  children,
}: AIUsageDashboardProviderProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState<DashboardFilters>({ period: "last_90_days" });

  const [kpis, setKpis] = useState<any | null>(null);
  const [collaborators, setCollaborators] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);

  const [isLoadingKpis, setIsLoadingKpis] = useState(false);
  const [isLoadingCollaborators, setIsLoadingCollaborators] = useState(false);

  const [error, setError] = useState<string>("");

  const service = useMemo(() => new AIUsageDashboardService(), []);

  const isLoading = isLoadingKpis || isLoadingCollaborators;

  // Sincronizar filtros com URL params
  useEffect(() => {
    const collaborator_id = searchParams.get("collaborator_id");
    const period = searchParams.get("period");
    const start_date = searchParams.get("start_date");
    const end_date = searchParams.get("end_date");
    const category_id = searchParams.get("category_id");

    setFilters({
      collaborator_id: collaborator_id ? Number(collaborator_id) : null,
      period,
      start_date,
      end_date,
      category_id: category_id ? Number(category_id) : null,
    });
  }, [searchParams]);

  // Atualizar URL quando filtros mudam
  const updateUrlParams = useCallback((newFilters: DashboardFilters) => {
    const params = new URLSearchParams();

    if (newFilters.collaborator_id)
      params.append("collaborator_id", String(newFilters.collaborator_id));
    if (newFilters.period) params.append("period", newFilters.period);
    if (newFilters.start_date) params.append("start_date", newFilters.start_date);
    if (newFilters.end_date) params.append("end_date", newFilters.end_date);
    if (newFilters.category_id)
      params.append("category_id", String(newFilters.category_id));

    setSearchParams(params);
  }, [setSearchParams]);

  // Buscar todos os dados em paralelo
  const fetchAllData = useCallback(async () => {
    try {
      setError("");

      // Remover valores nulos dos filtros
      const cleanFilters = Object.fromEntries(
        Object.entries(filters).filter(([_, value]) => value !== null && value !== undefined && value !== "")
      );

      setIsLoadingKpis(true);
      setIsLoadingCollaborators(true);

      const [kpisRes, kpisErr] = await service.getKpis(cleanFilters);
      const [collaboratorsRes, collaboratorsErr] = await service.getCollaborators(cleanFilters);

      setIsLoadingKpis(false);
      setIsLoadingCollaborators(false);

      if (kpisErr) {
        setError(kpisRes?.message || kpisErr);
        setKpis(null);
      } else {
        setKpis(kpisRes?.data);
      }

      if (collaboratorsErr) {
        setError(collaboratorsRes?.message || collaboratorsErr);
        setCollaborators([]);
      } else {
        setCollaborators(collaboratorsRes?.data?.collaborators || []);
      }
    } catch (e: any) {
      setIsLoadingKpis(false);
      setIsLoadingCollaborators(false);
      setError("Erro ao carregar dados do dashboard");
    }
  }, [filters, service]);


  // Buscar dados ao montar ou quando filtros mudam
  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  // Mostrar erro em toast
  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  // Sobrescrever setFilters para atualizar URL também
  const setFiltersWithUrl = useCallback((newFilters: DashboardFilters | ((prev: DashboardFilters) => DashboardFilters)) => {
    if (typeof newFilters === "function") {
      setFilters((prev) => {
        const updated = newFilters(prev);
        updateUrlParams(updated);
        return updated;
      });
    } else {
      setFilters(newFilters);
      updateUrlParams(newFilters);
    }
  }, [updateUrlParams]);

  return (
    <AIUsageDashboardContext.Provider
      value={{
        filters,
        setFilters: setFiltersWithUrl,
        kpis,
        collaborators,
        categories,
        isLoadingKpis,
        isLoadingCollaborators,
        isLoading,
        error,
        setError,
        fetchAllData,
      }}
    >
      {children}
    </AIUsageDashboardContext.Provider>
  );
};

export const useAIUsageDashboard = () =>
  useContext(AIUsageDashboardContext);
